import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import apiService from "@/services/apiService";
import { applyAnalyticsConsent } from "@/lib/analytics";
import {
  CookieChoices,
  OPEN_COOKIE_SETTINGS_EVENT,
  POLICY_VERSIONS,
  readConsent,
  saveConsent,
} from "@/lib/consent";

/** Logs a signed-in user's cookie choice as proof of consent. Best-effort. */
export async function recordCookieChoice(choices: CookieChoices): Promise<void> {
  try {
    await apiService.post("/policy-acceptances", {
      policy: "cookies",
      version: POLICY_VERSIONS.cookies,
      choices,
    }, false);
  } catch {
    /* the choice still applies locally; it is re-sent after the next sign-in */
  }
}

/**
 * Asks before any non-essential cookie is set. "Reject" is as prominent as
 * "Accept", nothing is pre-ticked, and the same panel reopens from "Cookie
 * settings" so consent can be withdrawn as easily as it was given.
 */
export default function CookieConsentBanner() {
  const auth = useAuth() as any;
  const [open, setOpen] = useState(() => readConsent() === null);
  const [customising, setCustomising] = useState(false);
  const [analytics, setAnalytics] = useState(() => readConsent()?.choices.analytics ?? false);

  useEffect(() => {
    const reopen = () => {
      setAnalytics(readConsent()?.choices.analytics ?? false);
      setCustomising(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
  }, []);

  if (!open) return null;

  const decide = (choices: CookieChoices) => {
    saveConsent(choices);
    applyAnalyticsConsent(choices.analytics);
    if (auth?.token) void recordCookieChoice(choices);
    setOpen(false);
    setCustomising(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[9998] p-3 sm:p-4 print:hidden"
    >
      <div className="mx-auto max-w-3xl rounded-xl border bg-background p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
          <div className="space-y-2 text-sm">
            <p className="font-semibold">Your privacy choices</p>
            <p className="text-muted-foreground">
              We use strictly necessary browser storage to keep you signed in and remember your settings. With your
              permission we would also use Google Analytics cookies to see which pages are used, by hospital and staff
              role. Patient details, record numbers and searches are never sent. You can change this at any time from
              "Cookie settings". See our{" "}
              <Link to="/cookie-policy" className="underline underline-offset-2">Cookie Policy</Link> and{" "}
              <Link to="/privacy-policy" className="underline underline-offset-2">Privacy Policy</Link>.
            </p>
          </div>
        </div>

        {customising && (
          <div className="mt-3 space-y-2 rounded-lg border p-3 text-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium">Strictly necessary</p>
                <p className="text-xs text-muted-foreground">
                  Sign-in session, your language and display settings, and this choice. The app cannot work without
                  them, so they are always on.
                </p>
              </div>
              <Switch checked disabled aria-label="Strictly necessary storage (always on)" />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium">Analytics</p>
                <p className="text-xs text-muted-foreground">
                  Google Analytics cookies (_ga, _ga_*) that count page views by hospital and staff role, with record
                  ids removed from page addresses. Off unless you turn it on.
                </p>
              </div>
              <Switch checked={analytics} onCheckedChange={setAnalytics} aria-label="Analytics cookies" />
            </div>
          </div>
        )}

        <div className="mt-3 flex flex-wrap justify-end gap-2">
          {customising ? (
            <Button size="sm" onClick={() => decide({ analytics })}>Save choices</Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setCustomising(true)}>Customise</Button>
          )}
          <Button size="sm" variant="outline" onClick={() => decide({ analytics: false })}>Reject non-essential</Button>
          <Button size="sm" variant="outline" onClick={() => decide({ analytics: true })}>Accept all</Button>
        </div>
      </div>
    </div>
  );
}
